/**
 * @file Shared enums for Payload CMS collections and application features.
 *
 * These enums are extracted from collection definitions to enable reuse across
 * the application, including the GuideGenerator feature.
 */

import { z } from "zod";

/**
 * Supported locales for the application.
 *
 * Uses BCP 47 language tags with region subtags.
 */
export const Locale = z.enum([
  `el-GR`,
  `en-CA`,
  `en-GB`,
  `en-US`,
  `es-419`,
  `es-ES`,
  `es-MX`,
  `fr-CA`,
  `fr-FR`,
]);
export type Locale = z.infer<typeof Locale>;

/**
 * CEFRL (Common European Framework of Reference for Languages) fluency levels.
 *
 * - A1, A2: Basic user (beginner to elementary)
 * - B1, B2: Independent user (intermediate to upper intermediate)
 * - C1, C2: Proficient user (advanced to mastery)
 */
export const Fluency = z.enum([
  `CEFRL_A1`,
  `CEFRL_A2`,
  `CEFRL_B1`,
  `CEFRL_B2`,
  `CEFRL_C1`,
  `CEFRL_C2`,
]);
export type Fluency = z.infer<typeof Fluency>;

/**
 * Target length for generated guides.
 *
 * - FLASH: Extremely short (~100-200 words)
 * - SHORT: Brief overview, key points only (~500-1000 words)
 * - MEDIUM: Standard guide with moderate detail (~1500-2500 words)
 * - LONG: Comprehensive guide with full detail (~5000-8000 words)
 */
export const GuideLength = z.enum([`FLASH`, `SHORT`, `MEDIUM`, `LONG`]);
export type GuideLength = z.infer<typeof GuideLength>;

/**
 * Human-readable label map for guide lengths.
 */
export const guideLengthLabelMap: Record<GuideLength, string> = {
  FLASH: `Flash (~100-200 words)`,
  LONG: `Long (~5000-8000 words)`,
  MEDIUM: `Medium (~1500-2500 words)`,
  SHORT: `Short (~500-1000 words)`,
};

/**
 * Human-readable label map for fluency levels.
 */
export const fluencyLabelMap: Record<Fluency, string> = {
  CEFRL_A1: `A1 - Beginner`,
  CEFRL_A2: `A2 - Elementary`,
  CEFRL_B1: `B1 - Intermediate`,
  CEFRL_B2: `B2 - Upper Intermediate`,
  CEFRL_C1: `C1 - Advanced`,
  CEFRL_C2: `C2 - Mastery`,
};

/**
 * Human-readable label map for locales.
 */
export const localeLabelMap: Record<Locale, string> = {
  "el-GR": `Greek`,
  "en-CA": `English (CA)`,
  "en-GB": `English (UK)`,
  "en-US": `English (US)`,
  "es-419": `Spanish (Latin America)`,
  "es-ES": `Spanish (Spain)`,
  "es-MX": `Spanish (Mexico)`,
  "fr-CA": `French (Canada)`,
  "fr-FR": `French (France)`,
};
